/**
 * Casino War bot. Casual play: a modest bet (a small multiple of the minimum, never more than a tenth of the stack),
 * an occasional minimum Tie side bet, and on a tie mostly going to war, which is the better play. Beginners sometimes
 * surrender. The bot only ever reads its own seat's view.
 */
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import type { CasinoWarAction, CasinoWarLegal, CasinoWarView } from './engine.js';

function chooseTie(legal: CasinoWarLegal, ctx: BotContext): CasinoWarAction {
  if (legal.kind !== 'tie') return { type: 'surrender' };
  const surrender = ctx.difficulty === 'BEGINNER' ? chance(ctx, 0.4) : ctx.difficulty === 'EASY' ? chance(ctx, 0.2) : false;
  return legal.canWar && !surrender ? { type: 'war' } : { type: 'surrender' };
}

export const casinoWarBot: BotEngine<CasinoWarView, CasinoWarLegal, CasinoWarAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'tie') return chooseTie(legal, ctx);
    return this.chooseBet!(view, legal, ctx);
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bet') return this.handleTimeout(view, legal, ctx);
    if (legal.canSitOut && chance(ctx, 0.03)) return { type: 'sitOut' };
    const me = view.seat === null ? null : view.players[view.seat];
    const multiple = 1 + ctx.rng.int(ctx.difficulty === 'BEGINNER' ? 4 : 3);
    const cap = me ? Math.max(legal.min, Math.floor(me.chips / 10)) : legal.min;
    const amount = Math.max(legal.min, Math.min(legal.min * multiple, cap, legal.max));
    const tieBet = legal.tieBetMax >= view.config.minBet && ctx.difficulty !== 'EXPERT' && chance(ctx, 0.08) && amount + view.config.minBet <= (me?.chips ?? 0) ? view.config.minBet : 0;
    return { type: 'bet', amount, tieBet };
  },

  evaluateState(view) {
    const me = view.seat === null ? null : view.players[view.seat];
    return me ? Math.max(0, Math.min(1, me.chips / (view.config.minBet * 200))) : 0;
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view) {
    return view.phase === 'DECIDING' ? { type: 'surrender' } : { type: 'sitOut' };
  },
};
